/**
 * upload/UploadOverlayClassic.tsx — the ORIGINAL upload overlay look, unchanged.
 *
 * This is the backend team's navy `--tb-*` styling, phase for phase. It is the DEFAULT skin and must
 * stay indistinguishable from the overlay as it shipped. All behaviour comes from `useUploadOverlay`;
 * this file is only the JSX that reads it (`vm.*`) plus the four presentational helpers that were only
 * ever used here (Progress, ResumePrompt, DuplicatePrompt, UploadDone).
 *
 * The reskin (UploadOverlayReskin.tsx) renders its own versions of these; the dispatcher
 * (UploadOverlay.tsx) picks between the two off the per-browser skin toggle.
 */
import { DetailsStage } from './DetailsStage';
import { InstructionsStage } from './InstructionsStage';
import { ReviewStage } from './ReviewStage';
import { draftsAvailable, type UploadDraft } from './draftStore';
import type { DuplicateCandidate, UploadResult } from '../api';
import {
  STAGE_LABELS,
  STAGE_ORDER,
  useUploadOverlay,
  type UploadOverlayProps,
} from './useUploadOverlay';

export function UploadOverlayClassic(props: UploadOverlayProps) {
  const vm = useUploadOverlay(props);
  // dialogRef is pulled into a local so the ref callback writes `dialogRef.current` (a mutable
  // RefObject slot) instead of `vm.dialogRef.current` — the React compiler treats the hook-return
  // `vm` as frozen and flags the reach-through assignment ("this value cannot be modified").
  const { phase, stage, details, explanations, components, dialogRef } = vm;

  return (
    <div
      className="tb-ov-backdrop"
      // A click on the backdrop itself closes; a click inside the dialog must not bubble out to it.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div
        className="tb-ov"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tb-ov-title"
        tabIndex={-1}
        ref={(el) => {
          dialogRef.current = el;
          // Focus the dialog as soon as it mounts, so the keyboard is inside it immediately.
          if (el && !el.dataset.focused) {
            el.dataset.focused = 'true';
            el.focus();
          }
        }}
        onKeyDown={vm.onKeyDown}
      >
        <div className="tb-ov-head">
          <div>
            <h2 id="tb-ov-title">Share “{props.flow.name}”</h2>
            <p className="tb-ov-sub">Power Automate cloud flow in {props.environmentName}</p>
          </div>
          <button type="button" className="tb-ov-close" onClick={props.onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="tb-ov-body">
          {phase.kind === 'wizard' && (
            <ol className="tb-steps">
              {STAGE_ORDER.map((s, i) => (
                <li
                  key={s}
                  aria-current={s === stage ? 'step' : undefined}
                  data-done={i < vm.stageIndex ? 'true' : 'false'}
                >
                  <span className="tb-step-n" aria-hidden="true">
                    {i < vm.stageIndex ? '✓' : i + 1}
                  </span>
                  {STAGE_LABELS[s]}
                </li>
              ))}
            </ol>
          )}

          {vm.errorMsg ? (
            <div className="tb-note tb-note--warn" role="alert">
              {vm.errorMsg}
            </div>
          ) : null}

          {!draftsAvailable() && phase.kind === 'wizard' ? (
            <div className="tb-note tb-note--warn" role="status">
              This browser will not let us save a draft, so your progress is not being kept. Finish
              in one go, or enable site data for this page.
            </div>
          ) : null}

          {phase.kind === 'loading' && <p role="status">Reading the steps of this automation…</p>}

          {phase.kind === 'unavailable' && (
            <div>
              <div className="tb-note tb-note--warn" role="alert">
                {phase.message}
              </div>
              <p>
                Nothing has been shared and nothing has been changed. Pick a different automation,
                or try again once the flow opens correctly in Power Automate.
              </p>
            </div>
          )}

          {phase.kind === 'resume' && (
            <ResumePrompt
              draft={phase.draft}
              componentCount={components.length}
              onResume={vm.onResume}
              onStartNew={vm.onStartNew}
            />
          )}

          {phase.kind === 'duplicates' && (
            <DuplicatePrompt
              candidates={phase.candidates}
              sameFlow={phase.sameFlow}
              onContinue={vm.onDuplicatesContinue}
              onAbandon={vm.onDuplicatesAbandon}
            />
          )}

          {phase.kind === 'wizard' && stage === 'details' && (
            <DetailsStage
              details={details}
              refData={props.refData}
              errors={vm.detailErrors}
              showErrors={vm.triedStage.details}
              requirementsRequired={vm.requirementsRequired}
              connectorState={props.connectorState}
              builderName={props.builderName}
              builderDepartment={props.builderDepartment}
              onChange={vm.onDetailsChange}
              registerField={vm.registerDetailField}
            />
          )}

          {phase.kind === 'wizard' && stage === 'instructions' && (
            <>
              <Progress explained={vm.explained} total={components.length} />
              <InstructionsStage
                components={components}
                explanations={explanations}
                missingIds={vm.missingIds}
                showErrors={vm.triedStage.instructions}
                collapsedIds={vm.collapsedIds}
                droppedCount={vm.droppedCount}
                onToggleCollapse={vm.onToggleCollapse}
                onChange={vm.onExplanationChange}
                registerField={vm.registerComponentField}
              />
            </>
          )}

          {phase.kind === 'wizard' && stage === 'review' && (
            <ReviewStage
              details={details}
              refData={props.refData}
              components={components}
              explanations={explanations}
              attested={vm.draft?.attested ?? false}
              takesResponsibility={vm.draft?.takesResponsibility ?? false}
              showErrors={vm.triedStage.review}
              onAttestChange={vm.onAttestChange}
              onResponsibilityChange={vm.onResponsibilityChange}
              onEditDetails={() => vm.goToStage('details')}
              onEditInstructions={() => vm.goToStage('instructions')}
              onEditComponent={vm.onEditComponent}
            />
          )}

          {phase.kind === 'done' && <UploadDone result={phase.result} />}
        </div>

        <div className="tb-ov-foot">
          {phase.kind === 'wizard' && stage === 'details' && (
            <>
              <span className="tb-muted">Step 1 of 3</span>
              <span className="tb-ov-spacer" />
              <button type="button" onClick={props.onClose}>
                Save and close
              </button>
              <button type="button" onClick={vm.onNextFromDetails}>
                Next: Instructions
              </button>
            </>
          )}

          {phase.kind === 'wizard' && stage === 'instructions' && (
            <>
              <span className="tb-muted">
                {vm.explained} of {components.length} steps explained
              </span>
              <span className="tb-ov-spacer" />
              <button type="button" onClick={() => vm.goToStage('details')}>
                Back
              </button>
              <button type="button" onClick={props.onClose}>
                Save and close
              </button>
              {/* Not disabled: pressing it is how the builder learns WHAT is missing and gets taken there. */}
              <button type="button" onClick={vm.onNextFromInstructions}>
                Next: Review
              </button>
            </>
          )}

          {phase.kind === 'wizard' && stage === 'review' && (
            <>
              <span className="tb-muted">Step 3 of 3</span>
              <span className="tb-ov-spacer" />
              <button type="button" onClick={() => vm.goToStage('instructions')}>
                Back
              </button>
              <button type="button" onClick={props.onClose} disabled={vm.submitting}>
                Save and close
              </button>
              <button type="button" onClick={() => void vm.onUpload()} disabled={vm.submitting}>
                {vm.submitting ? 'Sharing…' : 'Share this tool'}
              </button>
            </>
          )}

          {(phase.kind === 'unavailable' || phase.kind === 'done') && (
            <>
              <span className="tb-ov-spacer" />
              <button type="button" onClick={props.onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** The completion indicator. Bar plus text, because a bar alone conveys nothing to a screen reader. */
function Progress({ explained, total }: { explained: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((explained / total) * 100);
  const complete = explained === total && total > 0;
  return (
    <div className="tb-progress">
      <span className="tb-progress-text" data-complete={complete ? 'true' : 'false'}>
        {explained} of {total} steps explained
      </span>
      <div
        className="tb-progress-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={explained}
        aria-label="Steps explained"
      >
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Will's "Resume or Start New Prompt". */
function ResumePrompt(props: {
  draft: UploadDraft;
  componentCount: number;
  onResume: () => void;
  onStartNew: () => void;
}) {
  const saved = props.draft.updatedAt ? new Date(props.draft.updatedAt) : null;
  const explainedSoFar = Object.values(props.draft.explanations).filter(
    (v) => v.trim() !== '',
  ).length;

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>You have an unfinished draft for this automation</h3>
      <dl>
        <dt>Last saved</dt>
        <dd>
          {saved && !Number.isNaN(saved.valueOf()) ? saved.toLocaleString('en-GB') : 'Unknown'}
        </dd>
        <dt>Name so far</dt>
        <dd>
          {props.draft.details.name.trim() || <span className="tb-muted">Not yet named</span>}
        </dd>
        <dt>Steps explained</dt>
        <dd>
          {explainedSoFar} of {props.componentCount}
        </dd>
        <dt>Stopped on</dt>
        <dd>{STAGE_LABELS[props.draft.stage]}</dd>
      </dl>
      <p>
        Resuming keeps everything you wrote. Starting again clears this draft for this automation
        only — drafts for your other automations are untouched.
      </p>
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
        <button type="button" onClick={props.onResume}>
          Resume this draft
        </button>
        <button type="button" onClick={props.onStartNew}>
          Start new
        </button>
      </div>
    </div>
  );
}

/**
 * Will's "Duplicate found?" gate, implemented as UP-F27 requires: a soft, non-blocking warning that
 * shows the existing tool and lets the builder decide. Nothing here can prevent an upload.
 */
function DuplicatePrompt(props: {
  candidates: DuplicateCandidate[];
  sameFlow: boolean;
  onContinue: () => void;
  onAbandon: () => void;
}) {
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>
        {props.sameFlow
          ? 'This automation has already been shared'
          : 'Some similar tools already exist'}
      </h3>
      <p>
        {props.sameFlow
          ? 'Someone has already shared this exact flow as a tool. Have a look — if it does the same job, there is no need for a second copy.'
          : 'These look similar to what you are about to share. If one of them already does the job, colleagues are better served by improving it than by having two.'}
      </p>

      {props.candidates.map((c) => (
        <div className="tb-dup" key={c.toolId}>
          <h4>{c.name}</h4>
          <p className="tb-comp-meta">
            {c.matchKind === 'same_flow' ? 'Same automation' : 'Similar'} · {c.trustLabel} ·{' '}
            {c.mine ? 'shared by you' : `shared by ${c.ownerName}`}
          </p>
          <p>{c.description}</p>
          {/* Opens in a new tab so the in-progress overlay and its draft are not navigated away from. */}
          <a href={`/tools/${c.toolId}`} target="_blank" rel="noreferrer">
            Open this tool in a new tab
          </a>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.8rem' }}>
        <button type="button" onClick={props.onContinue}>
          Mine does something different — carry on
        </button>
        <button type="button" onClick={props.onAbandon}>
          It does the same thing — don’t share mine
        </button>
      </div>
    </div>
  );
}

/** The success panel. Mirrors the wording and fields the previous Share form ended on. */
function UploadDone({ result }: { result: UploadResult }) {
  const connectors = Array.isArray(result.required_connectors)
    ? result.required_connectors.map((c) => c.apiName).join(', ') || 'None'
    : 'Requirements could not be read';
  // The route echoes the REAL initial status the platform's moderation_mode routed to: 'Pending' when
  // the submission is held for the reviewer queue (manual_review), or 'Approved' when it publishes
  // with no human step (self_moderated). The copy follows the actual outcome so the builder knows what
  // happened and what comes next — it is not predicted from the responsibility tick.
  const heldForReview = result.publication_status === 'Pending';
  const publishedLive = result.publication_status === 'Approved';
  return (
    <div className="tb-note tb-note--ok" role="status">
      <h3 style={{ marginTop: 0 }}>{heldForReview ? 'Sent for verification' : 'Shared'}</h3>
      <dl>
        <dt>Tool id</dt>
        <dd className="tb-mono">{result.tool_id}</dd>
        <dt>Blueprint stored</dt>
        <dd>{result.has_clientdata ? 'Yes' : 'No'}</dd>
        <dt>Steps documented</dt>
        <dd>{result.documented_component_count ?? 0}</dd>
        <dt>Connectors required</dt>
        <dd>
          {result.connector_count} — {connectors}
        </dd>
      </dl>
      {heldForReview ? (
        <p>
          It is held with the <strong>AI SWAT team</strong> to check for confidentiality and
          security. Once they approve it, it is published as <strong>Approved</strong>. Your local
          draft has been cleared.
        </p>
      ) : publishedLive ? (
        <p>
          It is now published as <strong>Approved</strong> and visible in the catalogue. Your local
          draft has been cleared.
        </p>
      ) : (
        <p>
          It is saved as a <strong>Draft</strong>. Your local draft has been cleared.
        </p>
      )}
      <p>
        <a href={`/tools/${result.tool_id}`}>Open the tool detail page</a>
      </p>
    </div>
  );
}
